import { Component, Input, OnInit, ViewChild, ViewEncapsulation, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subscription, forkJoin, of } from 'rxjs';
import { DropDownListComponent } from '@syncfusion/ej2-angular-dropdowns';
import { RadioButtonComponent } from '@syncfusion/ej2-angular-buttons';
import { ILLCompany, ICompanyDetail, ILLCustomerCompaniesByTypeWrapper } from '../../../../shared/models';
import { LLFormService, CompanyContactService, StorageManagerService } from '../../../../shared/services';
import { ToastrService } from 'ngx-toastr';
import { RefdataService } from '../../../../shared/services/refdata.service';
import { catchError } from 'rxjs/operators';
import { CompanyDetailsComponent } from '../../../index';
import { DialogComponent } from '@syncfusion/ej2-angular-popups';

@Component({
    selector: 'app-ll-form-registrant',
    templateUrl: './ll-form-registrant.component.html',
    styleUrls: ['./ll-form-registrant.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class LLFormRegistrantComponent implements OnInit, AfterViewInit {

  registrantForm: UntypedFormGroup;
  registrant: ILLCompany = new ILLCompany();
  registrantCompanies: ILLCompany[] = [];
  rCompanyRevisionId: number;
  companyDataSource: any[] = [];
  iconTypeCompany = true;
  isCompanyInactive = false;
  inactiveLabelerContactName: string;
  sub: Subscription;
  hidden: Boolean = false;
  registrantConfidentiality = 'N';
  companyToViewRevisionId: number;
  parentComponent: any;
  customerGuid: string;
  companyData: any;

  @Input() llFormData: any;
  @Input() canCreateCompany: boolean;

  ddwHeight = '220px';
  animationSettings: Object = { effect: 'Zoom' };

  public waterMark = 'Select one';
  public companyFields: Object = { text: 'name', value: 'id' };
  @ViewChild('rCompanyDdw') companyDdw: DropDownListComponent;
  @ViewChild('companyDialog') public companyDialog: DialogComponent;
  @ViewChild('companyDetails') public companyDetailsComponent: CompanyDetailsComponent;
  @ViewChild('yesRadio') yesRadioBtn: RadioButtonComponent;
  @ViewChild('noRadio') noRadioBtn: RadioButtonComponent;

    constructor(private fb: UntypedFormBuilder, private llFormService: LLFormService, private toastr: ToastrService,
      private companyService: CompanyContactService, private cdref: ChangeDetectorRef,
      private refDataService: RefdataService, private storageManagerService: StorageManagerService) { }

  ngOnInit() {
    // First, determine the registrantConfidentiality value BEFORE creating the form
    this.determineRegistrantConfidentialityValue();

    // Create form with the correct initial value
    this.registrantForm = this.fb.group({
      rCompanyRevisionId: ['', { updateOn: 'blur' }],
      registrantConfidentiality: [this.registrantConfidentiality]
    });

    this.setCompaniesDdw();

    // Watches changes on the entire form
    this.registrantForm.valueChanges.subscribe(data => {
      this.rCompanyRevisionId = data['rCompanyRevisionId'];
      this.registrantConfidentiality = data['registrantConfidentiality'];

      this.llFormService.llFormRegistrantSubject.next(this.registrantForm.value);
    });

    if (this.llFormData && this.llFormData[0] && this.llFormData[0].data) {
      this.registrant = this.llFormData[0].data['registrantCompany'];
      this.rCompanyRevisionId = this.registrant?.companyRevisionId;

      // Check if previously saved company are not the latest revision, display warning message
      this.isCompanyInactive = !this.isCompanyLatest(this.registrantCompanies, this.rCompanyRevisionId);
      this.parentComponent = this;

      const currentCustomerStr: string = this.storageManagerService.read('currentCustomer');

      if (currentCustomerStr) {
        const currentCustomer = JSON.parse(currentCustomerStr);
        if (currentCustomer) {
          this.customerGuid = currentCustomer.customerGuid;
        }
      }
      
      this.patchForm();
    }

    this.llFormService.llFormRegistrantSubject.next(this.registrantForm.value);
  }

  /**
   * Determines the registrantConfidentiality value from the API response.
   * This must be called BEFORE creating the form.
   * The API returns this value in registrantCompany.registrantConfidentiality
   */
  private determineRegistrantConfidentialityValue(): void {
    this.registrantConfidentiality = 'N'; // Default value
    
    if (this.llFormData && this.llFormData[0] && this.llFormData[0].data) {
      const registrantCompany = this.llFormData[0].data['registrantCompany'];
      const confValue = registrantCompany?.registrantConfidentiality;
      
      // Normalize the value to 'Y' or 'N'
      if (confValue) {
        const normalized = confValue.toString().trim().toUpperCase();
        this.registrantConfidentiality = (normalized === 'Y') ? 'Y' : 'N';
      }
    }
  }

  patchForm() {
    // Set the form values - this will update the radio button state
    this.registrantForm.patchValue({
      rCompanyRevisionId: this.registrant?.companyRevisionId,
      registrantConfidentiality: this.registrantConfidentiality
    });
    
    // Mark as pristine since this is initial data load
    this.registrantForm.markAsPristine();
    
    // Trigger change detection to ensure UI updates
    this.cdref.detectChanges();
  }

  ngAfterViewInit(): void {
    this.companyDdw.value = this.rCompanyRevisionId;
    
    // Programmatically set the radio button checked state after view init
    setTimeout(() => {
      if (this.yesRadioBtn && this.noRadioBtn) {
        if (this.registrantConfidentiality === 'Y') {
          this.yesRadioBtn.checked = true;
          this.noRadioBtn.checked = false;
        } else {
          this.yesRadioBtn.checked = false;
          this.noRadioBtn.checked = true;
        }
        this.cdref.detectChanges();
      }
    }, 0);
    
    this.cdref.detectChanges();
  }

  setCompaniesDdw() {

    if (this.llFormData && this.llFormData[2].data) {

      const companyTypes: ILLCustomerCompaniesByTypeWrapper = this.llFormData[2].data;

      this.registrantCompanies = companyTypes.registrants;
    }

    if (this.registrantCompanies && this.registrantCompanies.length > 0) {

      this.companyDataSource.push({ id: 0, name: '' });

      this.registrantCompanies.forEach((company: ILLCompany) => {

        const ds = { id: 0, name: '' };
        ds.id = +company.companyRevisionId;
        ds.name = company.companyName;

        this.companyDataSource.push(ds);
      });
    }
  }

  ddwChange(event) {

    const registrant = this.registrantCompanies.find(c => c.companyRevisionId === event.value);

    this.llFormData[0].data.registrantCompany = registrant;

    this.registrantForm.markAsDirty();

    if (!registrant) {
      this.registrantConfidentialityChange('');
    }
  }

  registrantConfidentialityChange(value) {

    this.registrantConfidentiality = value;

    this.registrantForm.patchValue({
      registrantConfidentiality: value
    });

    // Update the top-level registrantConfidentiality field
    this.llFormData[0].data.registrantConfidentiality = this.registrantConfidentiality;

    // Also update the nested registrantCompany object so the value is included in the
    // validate/save payload (the backend reads registrantCompany.registrantConfidentiality).
    if (this.llFormData[0].data.registrantCompany) {
      this.llFormData[0].data.registrantCompany.registrantConfidentiality = this.registrantConfidentiality;
    }

    this.registrantForm.markAsDirty();
  }

    // Check if the previously saved company is the latest version
    isCompanyLatest(companyLov: any[], companyRevisionId: number) {
        let returnVal = true;

        if (companyRevisionId) {
            const list = companyLov.filter(x => x.companyRevisionId === companyRevisionId);
            if (list.length === 0) {
                // Get company name and duns number
                this.sub = this.companyService.getCompanyById(companyRevisionId.toString()).subscribe(data => {
                    if (data && data['companyDetails']) {
                        const companyDetail: ICompanyDetail = data['companyDetails'];
                      this.inactiveLabelerContactName = `(${ companyDetail.duns }) ${ companyDetail.companyName }`;
                      // Add this check to avoid error "Attempt to use a destroyed view: detectChanges"
                      if (!this.cdref['destroyed']) {
                        this.cdref.detectChanges();
                      }
                    }
                },
                    error => this.toastr.error(error.errorMessage)
                );

                returnVal = false;
            }
        }
        return returnVal;
  }
  showCompanyDialog(companyRevisionId?: number) {

    this.registrantForm.markAsDirty();
    // this.pageLoading = true;

       if (companyRevisionId) {

        const companyData$ = forkJoin([this.companyService.getBusinessCategories(false),
          this.companyService.getCountries(),
          this.companyService.getContacts(),
          this.refDataService.GetAllBusinessOperationQualifier(),
          this.companyService.getCompanyRevisionHistory(companyRevisionId),
          this.companyService.getBusinessOperationQualifierByCompany(companyRevisionId.toString()),
          this.companyService.getCompanyById(companyRevisionId.toString()),
          this.companyService.getNDCLabelerCodesByCompany(companyRevisionId.toString())]
      ).pipe(catchError(err => of(err)
        )).subscribe(data => {

          if (this.companyDetailsComponent) {

            this.companyDetailsComponent.companyData = data;
            this.companyDetailsComponent.companyRevisionId = companyRevisionId.toString();
            this.companyDetailsComponent.populateDataFromResolver();
            this.companyDetailsComponent.setButtons();
            this.companyDetailsComponent.getFormControlByName('status').patchValue('Active');
            this.companyDetailsComponent.checkUserPermission();
            this.companyDetailsComponent.ngAfterViewInit();
          }

          // this.pageLoading = false;
          this.companyDialog.show();
        });
    } else {
      const companyData = [];

      this.sub = this.companyService.getBusinessCategories(false).subscribe( busCat => {
        companyData[0] = busCat;

        this.sub = this.companyService.getCountries().subscribe( countries => {
          companyData[1] = countries;

          this.sub = this.companyService.getContacts().subscribe( contacts => {
            companyData[2] = contacts;

             this.refDataService.GetAllBusinessOperationQualifier().subscribe(busQual => {

              companyData[3] = busQual;

              if (this.companyDetailsComponent) {

                this.companyDetailsComponent.companyData = companyData;
                this.companyDetailsComponent.companyRevisionId = '';
                this.companyDetailsComponent.company = null;
                this.companyDetailsComponent.companyHistory_grid_data = [];
                this.companyDetailsComponent.populateDataFromResolver();
                this.companyDetailsComponent.setButtons();
                this.companyDetailsComponent.checkUserPermission();
                this.companyDetailsComponent.ngAfterViewInit();
               }

               // this.pageLoading = false;
               this.companyDialog.show();

             });
          });
        });
      },
        error => {
          this.toastr.error(error.message ? error.message : this.llFormService.GENERIC_ERROR_MSG);
        });
    }
  }

  modalCompany(newCompany) {

    this.setSavedRegistrantCompany(newCompany.companyRevisionId);

    this.companyDialogCancelButtonClicked();
  }


  setSavedRegistrantCompany(companyRevisionId: any) {
    let company;

    this.sub = this.llFormService.getLCompanyTypesByCustomerGuid(this.customerGuid).subscribe(response => {

      const companyTypes = response['data'];

      this.llFormData[2].data = companyTypes;
      this.registrantCompanies = companyTypes['registrants'];

      company = this.registrantCompanies.find(c => c.companyRevisionId === companyRevisionId);

      if (company) {

        this.companyDataSource = [];
        this.registrantForm.patchValue({
          companyDdw: companyRevisionId,
        });

        this.setCompaniesDdw();

        //this.companyDataSource.sort(this.compare);

        this.companyDdw.dataSource = this.companyDataSource;
        this.companyDdw.dataBind();
        this.companyDdw.refresh();

        this.companyDdw.value = companyRevisionId;

      }
      this.cdref.detectChanges();
    },
      error => {
        this.toastr.error(error.message);
      });

    this.registrantForm.markAsDirty();
  }

  companyDialogCancelButtonClicked() {

    if (this.companyDetailsComponent) {

      this.companyDetailsComponent.companyForm.clearValidators();
    }
    this.companyDialog.hide();
  }

   compare(a, b) {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.bame) {
      return 1;
    }
    return 0;
  }
}
